<script setup>
import { computed } from 'vue'

const props = defineProps({
    /**
     * Visual style variant.
     * - primary: filled navy (default for main CTAs)
     * - secondary: outlined navy on white (supporting actions)
     * - ghost: text-only with arrow (inline tertiary links, "Read more →" style)
     */
    variant: {
        type: String,
        default: 'primary',
        validator: (v) => ['primary', 'secondary', 'ghost'].includes(v),
    },
    /**
     * Size of the button. Affects padding and font-size.
     * Ghost variant ignores size and uses inline sizing.
     */
    size: {
        type: String,
        default: 'md',
        validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    /**
     * If provided, renders as <a>. Otherwise renders as <button>.
     */
    href: {
        type: String,
        default: null,
    },
    /**
     * Button HTML type. Only applies when rendering as <button>.
     */
    type: {
        type: String,
        default: 'button',
    },
    /**
     * Make the button stretch to fill its container width.
     */
    block: {
        type: Boolean,
        default: false,
    },
    /**
     * Disabled state — only applies to <button>. Visually dims and blocks pointer events.
     */
    disabled: {
        type: Boolean,
        default: false,
    },
})

const tag = computed(() => (props.href ? 'a' : 'button'))

const baseClasses =
    'inline-flex items-center justify-center font-extrabold tracking-wider uppercase transition-colors ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-900 focus-visible:ring-offset-2 ' +
    'disabled:cursor-not-allowed disabled:opacity-60'

const sizeClasses = {
    sm: 'px-4 py-2 text-xs rounded-md',
    md: 'px-6 py-3.5 text-sm rounded-md',
    lg: 'px-7 py-4 text-base rounded-md',
}

const variantClasses = {
    primary: 'bg-brand-900 text-white hover:bg-brand-800 shadow-sm',
    secondary: 'bg-white text-brand-900 border border-brand-900 hover:bg-brand-50',
    ghost: 'text-brand-900 hover:text-brand-700 normal-case tracking-normal font-semibold',
}

const classes = computed(() => {
    const cls = [baseClasses, variantClasses[props.variant]]

    if (props.variant !== 'ghost') {
        cls.push(sizeClasses[props.size])
    }

    if (props.block) {
        cls.push('w-full')
    }

    return cls.join(' ')
})
</script>

<template>
    <component
        :is="tag"
        :class="classes"
        :href="href"
        :type="tag === 'button' ? type : null"
        :disabled="tag === 'button' ? disabled : null"
    >
        <slot />
    </component>
</template>
